import { createSlice } from "@reduxjs/toolkit";
import {
  generateReportThunk,
  generateXarReportThunk,
  listXarReportThunk,
  resolveAddressThunk
} from "./reportsActions";

export interface IReportsFilterData {
  startDate?: string;
  endDate?: string;
  object?: string[];
  objectType?: string[];
  group?: string[];
  key?: string[];
  daysOfWeek?: number[];
  startTime?: string;
  endTime?: string;
  reportType?: number;
  rideModes?: number[];
  startedWithoutIgnition?: boolean;
  /** Client-only: FE filter preference; stripped before POST /reports and export. */
  showInactiveObjects?: boolean;
  [key: string]: any;
}

export interface IReportsDateValues {
  startDate: string;
  endDate: string;
}

export interface IReportsState {
  loading: boolean;
  resolvedAddressLoading: boolean;
  report: Array<any>;
  xarReport: Array<any>;
  xarReportLoading: boolean;
  resolvedAddresses: Array<any>;
  reportsFilterData: IReportsFilterData;
  hasExceededLimit: boolean | null;
  reportsDateValues: IReportsDateValues;
  activeGenerateRequestId: string | null;
}

const initialState: IReportsState = {
  loading: false,
  resolvedAddressLoading: false,
  report: [],
  xarReport: [],
  xarReportLoading: false,
  resolvedAddresses: [],
  reportsFilterData: {},
  reportsDateValues: {
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString()
  },
  hasExceededLimit: null,
  activeGenerateRequestId: null
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    resetReports: (state) => {
      state.report = [];
      state.loading = false;
      state.hasExceededLimit = null;
      state.xarReport = [];
      state.xarReportLoading = false;
    },
    setReportsFilterData: (state, action) => {
      state.reportsFilterData = action.payload;
    },
    resetReportsFilterData: (state) => {
      state.reportsFilterData = {};
    },
    setReportsDateValues: (state, action) => {
      state.reportsDateValues = {
        startDate:
          action.payload.startDate instanceof Date
            ? action.payload.startDate.toISOString()
            : action.payload.startDate,
        endDate:
          action.payload.endDate instanceof Date
            ? action.payload.endDate.toISOString()
            : action.payload.endDate
      };
    },
    resetReportsDateValues: (state) => {
      const now = new Date().toISOString();
      state.reportsDateValues = {
        startDate: now,
        endDate: now
      };
    }
  },
  extraReducers: (builder) => {
    // Get Reports Action Cases
    builder.addCase(generateReportThunk.pending, (state, action) => {
      state.loading = true;
      state.activeGenerateRequestId = action.meta.requestId;
    });
    builder.addCase(generateReportThunk.fulfilled, (state, action: any) => {
      if (state.activeGenerateRequestId !== action.meta.requestId) return;
      state.report = action.payload.data;
      state.hasExceededLimit = action.payload.hasExceededLimit;
      state.loading = false;
      state.activeGenerateRequestId = null;
    });
    builder.addCase(generateReportThunk.rejected, (state, action) => {
      if (state.activeGenerateRequestId !== action.meta.requestId) return;
      state.loading = false;
      state.activeGenerateRequestId = null;
    });

    //list Xar reports
    builder.addCase(listXarReportThunk.pending, (state) => {
      state.xarReportLoading = true;
    });
    builder.addCase(listXarReportThunk.fulfilled, (state, action: any) => {
      state.xarReport = action.payload.items;
      state.xarReportLoading = false;
    });
    builder.addCase(listXarReportThunk.rejected, (state) => {
      state.xarReportLoading = false;
    });

    //generate Xar reports
    builder.addCase(generateXarReportThunk.pending, (state) => {
      state.xarReportLoading = true;
    });
    builder.addCase(generateXarReportThunk.fulfilled, (state, action: any) => {
      // state.xarReport = action.payload.data;
      state.xarReportLoading = false;
    });
    builder.addCase(generateXarReportThunk.rejected, (state) => {
      state.xarReportLoading = false;
    });

    builder.addCase(resolveAddressThunk.pending, (state) => {
      state.resolvedAddressLoading = true;
    });
    builder.addCase(resolveAddressThunk.fulfilled, (state, action: any) => {
      const { latitude, longitude } = action.meta.arg;
      const alreadyStored = state.resolvedAddresses.some(
        (item) => item.latitude === latitude && item.longitude === longitude
      );

      if (!alreadyStored) {
        const payload = action.payload;
        const address =
          typeof payload === "string"
            ? payload
            : payload?.resolvedAddress || payload?.address || "";

        state.resolvedAddresses = [
          ...state.resolvedAddresses,
          {
            latitude,
            longitude,
            address
          }
        ];
      }
      state.resolvedAddressLoading = false;
    });
    builder.addCase(resolveAddressThunk.rejected, (state, action: any) => {
      const { latitude, longitude } = action.meta.arg || {};
      if (
        typeof latitude === "number" &&
        typeof longitude === "number" &&
        !state.resolvedAddresses.some(
          (item) => item.latitude === latitude && item.longitude === longitude
        )
      ) {
        // Persist a completed attempt so UI can stop waiting on this coordinate.
        state.resolvedAddresses = [
          ...state.resolvedAddresses,
          {
            latitude,
            longitude,
            address: ""
          }
        ];
      }
      state.resolvedAddressLoading = false;
    });
  }
});

export * from "./reportsActions";
export const {
  resetReports,
  setReportsFilterData,
  resetReportsFilterData,
  setReportsDateValues,
  resetReportsDateValues
} = reportsSlice.actions;
export default reportsSlice.reducer;
